import Image from "next/image";

import styles from "./ExtendedReceiptViewPopup.module.css";

import { Receipt, ExtendedViewItem } from "@/types/receipt";
import { PopupArticle } from "@/constants/popup";

import {
  aggregateArticles,
  getActionableButtonText,
  getSharingWarningText,
  getStoreName,
} from "@/utils/extendedViewHelpers";

import { Button } from "@/components/common";
import { useExtendedServiceHandler } from "@/hooks/useExtendedServiceHandler";
import { useActiveAccountingWidget } from "@/hooks/useActiveAccountingWidget";
import { hasEmailExportFor } from "@/config/emailExportWidgets";
import { EmailExportSection } from "./EmailExportSection";

interface ExtendedReceiptViewPopupProps {
  extendedViewItem: ExtendedViewItem;
  extendedViewImage?: string;
  lang: string;
  user_id?: string;
  receipt_id?: string;
  receipt: Receipt;
  email?: string | null;
  phoneNumber?: string | null;
  article?: PopupArticle;
  closeButton?: React.ReactNode;
}

export const ExtendedReceiptViewPopup = ({
  extendedViewItem,
  extendedViewImage,
  lang,
  user_id,
  receipt_id,
  receipt,
  email,
  phoneNumber,
  article,
  closeButton,
}: ExtendedReceiptViewPopupProps) => {
  const { handleServiceClick, isLoading } = useExtendedServiceHandler({
    receipt,
    lang,
    user_id,
    receipt_id,
    email,
    phoneNumber,
  });
  const { getStoredEmail, onAccountingSendSuccess } =
    useActiveAccountingWidget();
  const itemKey = extendedViewItem.extended_view_position;
  const storeName = getStoreName(receipt);
  const hasEmailExport = hasEmailExportFor(
    user_id,
    receipt_id,
    extendedViewItem.receipt_view_service_settings_id
  );

  const handleClick = () => {
    let articles = null;

    if (extendedViewItem.extended_view_position === "website") {
      articles = aggregateArticles(receipt);
    } else if (
      article?.art_number != null &&
      article?.art_final_price != null
    ) {
      articles = [
        {
          art_number: article.art_number,
          art_final_price: article.art_final_price,
        },
      ];
    }

    handleServiceClick(extendedViewItem, itemKey, {
      articles,
      serviceViewPosition: null,
    });
  };

  const showEmailWarning = email && extendedViewItem.share_url_email;
  const showPhoneWarning =
    phoneNumber && extendedViewItem.share_url_phone_number;

  return (
    <>
      <div className="box head">
        <h4 className={styles.header}>
          {extendedViewImage && (
            <Image
              src={extendedViewImage}
              alt={extendedViewItem.title}
              width={32}
              height={32}
              className={styles.logo}
            />
          )}
          {extendedViewItem.title}
        </h4>
       
        {closeButton}
      </div>

      <div className="box">
        <p className={styles.description}>{extendedViewItem.description}</p>

        {user_id && receipt_id && (
          <EmailExportSection
            userId={user_id}
            receiptId={receipt_id}
            lang={lang}
            settingsId={extendedViewItem.receipt_view_service_settings_id}
            storeName={storeName}
            initialEmail={getStoredEmail(
              extendedViewItem.receipt_view_service_settings_id
            )}
            onSendSuccess={onAccountingSendSuccess(extendedViewItem)}
          />
        )}

        {(showEmailWarning || showPhoneWarning) && (
          <div className={styles.warningBox}>
            {showEmailWarning && (
              <p className={styles.warningText}>
                {getSharingWarningText(
                  extendedViewItem.extended_view_position,
                  "email",
                  lang
                )}
              </p>
            )}
            {showPhoneWarning && (
              <p
                className={
                  showEmailWarning
                    ? styles.warningTextWithMargin
                    : styles.warningText
                }
              >
                {getSharingWarningText(
                  extendedViewItem.extended_view_position,
                  "phone",
                  lang
                )}
              </p>
            )}
          </div>
        )}

        {!hasEmailExport && (
          <div className={styles.buttonContainer}>
            <Button
              type="button"
              onClick={handleClick}
              className={styles.actionButton}
              data-action-button="true"
              text={getActionableButtonText(
                extendedViewItem.extended_view_position,
                lang,
                {
                  extended_receipt_view_button_text:
                    extendedViewItem.extended_receipt_view_button_text,
                }
              )}
              isLoading={isLoading(itemKey)}
            />
          </div>
        )}
      </div>
    </>
  );
};
